import { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGlossary } from '../contexts/GlossaryContext';
import {
  createDhanurvedaPaymentOrder,
  verifyDhanurvedaPayment,
} from '../lib/api';
import type { DhanurvedaPaymentOrderRequest } from '../lib/types';
import '../styles/DhanurvedaBooking.css';

interface Props {
  onNavigate: (path: string) => void;
}

const BATCHES = [
  { id: 'spring2025',  name: 'वसन्त सत्र',  dates: 'मई २०–जून ५, २०२५',         price: '₹२,७००', advance: 500 },
  { id: 'autumn2025',  name: 'शरद सत्र',    dates: 'सित. १५–अक्टू. १, २०२५',    price: '₹२,७००', advance: 500 },
  { id: 'winter2025',  name: 'हेमन्त सत्र', dates: 'दिसम्बर १०–२६, २०२५',       price: '₹३,२००', advance: 500 },
];

type BillingCountryValue = 'IN' | 'ZZ';

interface BookingForm {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  billingCountry: BillingCountryValue;
  emergencyName: string;
  emergencyPhone: string;
  medicalNotes: string;
  ageConfirmed: boolean;
  healthDeclaration: boolean;
  riskAccepted: boolean;
  termsAccepted: boolean;
}

interface BookingConfirmation {
  bookingRef: string;
  paymentId: string;
  orderId: string;
  baseAmount: number;
  gstAmount: number;
  totalAmount: number;
  billingCountry: BillingCountryValue;
  gstApplicable: boolean;
  notificationStatus?: string;
  zohoLeadId?: string | null;
}

type PaymentStage = 'idle' | 'creating' | 'opening' | 'verifying';

function formatMoney(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Razorpay requires a browser environment.'));
      return;
    }
    if ((window as Window & { Razorpay?: unknown }).Razorpay) {
      resolve();
      return;
    }

    const existing = document.getElementById('razorpay-checkout-js') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay checkout.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
}

function generateRef(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return 'DV-' + Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function DhanurvedaBooking({ onNavigate }: Props) {
  const { t } = useGlossary();
  const { user, profile } = useAuth();

  const [selectedBatch, setSelectedBatch] = useState('spring2025');
  const [form, setForm] = useState<BookingForm>({
    fullName: profile?.alias || '',
    email: user?.email || '',
    phone: '',
    city: '',
    billingCountry: 'IN',
    emergencyName: '',
    emergencyPhone: '',
    medicalNotes: '',
    ageConfirmed: false,
    healthDeclaration: false,
    riskAccepted: false,
    termsAccepted: false,
  });
  const [confirmation, setConfirmation] = useState<BookingConfirmation | null>(null);
  const [paymentStage, setPaymentStage] = useState<PaymentStage>('idle');
  const [paymentError, setPaymentError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const batch = BATCHES.find(b => b.id === selectedBatch) ?? BATCHES[0];
  const batchLabel = {
    name: t(`dhanurveda.booking.batch.${selectedBatch}.name`),
    dates: t(`dhanurveda.booking.batch.${selectedBatch}.dates`),
    price: t(`dhanurveda.booking.batch.${selectedBatch}.price`),
  };
  const paymentSummary = useMemo(() => {
    const gstRate = form.billingCountry === 'IN' ? 0.18 : 0;
    const gstAmount = Math.round(batch.advance * gstRate);
    const totalAmount = batch.advance + gstAmount;
    return {
      gstAmount,
      totalAmount,
      jurisdictionLabel: form.billingCountry === 'IN'
        ? t('dhanurveda.booking.jurisdiction.india')
        : t('dhanurveda.booking.jurisdiction.outside'),
    };
  }, [batch.advance, form.billingCountry, t]);

  function update(field: keyof BookingForm, value: string | boolean) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const e = {...prev}; delete e[field]; return e; });
  }

  function validate() {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = t('dhanurveda.booking.error.fullName');
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = t('dhanurveda.booking.error.email');
    if (!form.phone.trim() || !/^\+?[0-9]{10,13}$/.test(form.phone.replace(/\s/g,'')))
      e.phone = t('dhanurveda.booking.error.phone');
    if (!form.city.trim()) e.city = t('dhanurveda.booking.error.city');
    if (!form.emergencyName.trim()) e.emergencyName = t('dhanurveda.booking.error.emergencyName');
    if (!form.emergencyPhone.trim() || !/^\+?[0-9]{10,13}$/.test(form.emergencyPhone.replace(/\s/g,'')))
      e.emergencyPhone = t('dhanurveda.booking.error.emergencyPhone');
    if (!form.ageConfirmed) e.ageConfirmed = t('dhanurveda.booking.error.ageConfirmed');
    if (!form.healthDeclaration) e.healthDeclaration = t('dhanurveda.booking.error.healthDeclaration');
    if (!form.riskAccepted) e.riskAccepted = t('dhanurveda.booking.error.riskAccepted');
    if (!form.termsAccepted) e.termsAccepted = t('dhanurveda.booking.error.termsAccepted');
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPaymentError('');
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const ref = generateRef();
    setPaymentStage('creating');

    const payload: DhanurvedaPaymentOrderRequest = {
      bookingRef: ref,
      batchId: selectedBatch,
      batchName: batchLabel.name,
      batchDates: batchLabel.dates,
      fullName: form.fullName,
      email: form.email,
      phone: form.phone,
      city: form.city,
      billingCountry: form.billingCountry,
      emergencyName: form.emergencyName,
      emergencyPhone: form.emergencyPhone,
      advanceAmount: batch.advance,
      uid: user?.uid || undefined,
      medicalNotes: form.medicalNotes || undefined,
      ageConfirmed: form.ageConfirmed,
      healthDeclaration: form.healthDeclaration,
      riskAccepted: form.riskAccepted,
      termsAccepted: form.termsAccepted,
    };

    try {
      const order = await createDhanurvedaPaymentOrder(payload);
      if (!order.order_id || !order.key_id) {
        throw new Error(t('dhanurveda.booking.error.paymentSetup'));
      }

      await loadRazorpayScript();
      const RazorpayCtor = (window as Window & { Razorpay?: new (options: any) => { open: () => void; on: (event: string, cb: (response: any) => void) => void } }).Razorpay;
      if (!RazorpayCtor) {
        throw new Error(t('dhanurveda.booking.error.paymentSetup'));
      }

      setPaymentStage('opening');
      const razorpay = new RazorpayCtor({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'raajadharma.org',
        description: `${batchLabel.name} · ${batchLabel.dates}`,
        order_id: order.order_id,
        prefill: {
          name: form.fullName,
          email: form.email,
          contact: form.phone,
        },
        notes: {
          booking_ref: ref,
          billing_country: order.billing_country,
          batch_name: batchLabel.name,
        },
        theme: { color: '#d4700a' },
        modal: {
          ondismiss: () => {
            setPaymentStage('idle');
            setPaymentError(t('dhanurveda.booking.error.paymentFailed'));
          },
        },
        handler: async (response: any) => {
          setPaymentStage('verifying');
          try {
            const result = await verifyDhanurvedaPayment({
              orderId: response?.razorpay_order_id || order.order_id,
              paymentId: response?.razorpay_payment_id,
              signature: response?.razorpay_signature,
            });

            const booking = {
              ref,
              paymentId: result.payment_id,
              orderId: result.order_id,
              batch: selectedBatch,
              batchName: batchLabel.name,
              batchDates: batchLabel.dates,
              advance: result.total_amount,
              baseAmount: result.base_amount,
              gstAmount: result.gst_amount,
              ...form,
              billingCountry: result.billing_country,
              submittedAt: new Date().toISOString(),
              status: 'paid',
              uid: user?.uid || 'guest',
            };
            const existing = JSON.parse(localStorage.getItem('dv_bookings') || '[]');
            localStorage.setItem('dv_bookings', JSON.stringify([...existing, booking]));

            setConfirmation({
              bookingRef: ref,
              paymentId: result.payment_id,
              orderId: result.order_id,
              baseAmount: result.base_amount,
              gstAmount: result.gst_amount,
              totalAmount: result.total_amount,
              billingCountry: (result.billing_country === 'IN' ? 'IN' : 'ZZ'),
              gstApplicable: result.gst_applicable,
              notificationStatus: result.notification_status,
              zohoLeadId: result.zoho_lead_id ?? null,
            });
          } catch (error) {
            setPaymentError(error instanceof Error ? error.message : t('dhanurveda.booking.error.paymentFailed'));
            setPaymentStage('idle');
          }
        },
      });

      razorpay.on('payment.failed', (response: any) => {
        setPaymentStage('idle');
        setPaymentError(response?.error?.description || t('dhanurveda.booking.error.paymentFailed'));
      });

      razorpay.open();
    } catch (error) {
      setPaymentStage('idle');
      setPaymentError(error instanceof Error ? error.message : t('dhanurveda.booking.error.paymentFailed'));
    }
  }

  if (!user) {
    return (
      <div className="dv-booking-page">
        <div className="dv-booking-header">
          <h1>{t('dhanurveda.booking.guest.title')}</h1>
          <p>{t('dhanurveda.booking.guest.subtitle')}</p>
        </div>
        <div className="dv-booking-form" style={{textAlign:'center', padding:'2.5rem'}}>
          <p style={{fontSize:'1rem', color:'#5c3d11', marginBottom:'1.5rem'}}>
            {t('dhanurveda.booking.guest.body')}
          </p>
          <button
            className="dv-submit-btn"
            style={{maxWidth:260}}
            onClick={() => onNavigate('/')}
          >
            {t('dhanurveda.booking.button.home')}
          </button>
        </div>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="dv-booking-page">
        <div className="dv-booking-confirmation">
          <div className="dv-confirmation-icon">🎯</div>
          <h2>{t('dhanurveda.booking.confirm.title')}</h2>
          <p>{t('dhanurveda.booking.confirm.paymentBody')}</p>
          <div className="dv-booking-ref">{confirmation.bookingRef}</div>

          <div className="dv-payment-instructions">
            <h3>{t('dhanurveda.booking.confirm.paymentTitle')}</h3>
            <div className="dv-payment-summary-card">
              <div className="dv-summary-row">
                <span className="dv-summary-label">{t('dhanurveda.booking.confirm.paymentReference')}</span>
                <span className="dv-summary-value">{confirmation.paymentId}</span>
              </div>
              <div className="dv-summary-row">
                <span className="dv-summary-label">{t('dhanurveda.booking.confirm.amountPaid')}</span>
                <span className="dv-summary-value">{formatMoney(confirmation.totalAmount)}</span>
              </div>
              <div className="dv-summary-row">
                <span className="dv-summary-label">{t('dhanurveda.booking.confirm.countryNote')}</span>
                <span className="dv-summary-value">
                  {confirmation.billingCountry === 'IN' ? t('dhanurveda.booking.option.india') : t('dhanurveda.booking.option.outside')}
                </span>
              </div>
              <div className="dv-summary-row">
                <span className="dv-summary-label">{t('dhanurveda.booking.summary.gst')}</span>
                <span className="dv-summary-value">{formatMoney(confirmation.gstAmount)}</span>
              </div>
            </div>
            <p className="dv-payment-note">{confirmation.gstApplicable ? t('dhanurveda.booking.jurisdiction.india') : t('dhanurveda.booking.confirm.gstNote')}</p>
            <p className="dv-payment-note">
              {t('dhanurveda.booking.confirm.referenceNote')}
            </p>
          </div>

          <div style={{display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap'}}>
            <button
              className="dv-submit-btn"
              style={{maxWidth:220}}
              onClick={() => onNavigate('/rajdharma/dhanurveda')}
            >
              {t('dhanurveda.booking.button.program')}
            </button>
            <button
              className="dv-submit-btn"
              style={{maxWidth:220, background:'linear-gradient(135deg,#2d5016,#1a3a0a)'}}
              onClick={() => onNavigate('/')}
            >
              {t('dhanurveda.booking.button.home')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dv-booking-page">
      <button
        style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem',
          margin:'1.2rem 0 0.5rem', padding:'0.4rem 0.9rem',
          border:'1.5px solid #8b5e3c', borderRadius:'24px',
          background:'transparent', color:'#8b5e3c', fontSize:'0.85rem',
          cursor:'pointer' }}
        onClick={() => onNavigate('/rajdharma/dhanurveda')}
      >
        {t('dhanurveda.booking.button.program')}
      </button>

        <div className="dv-booking-header">
        <h1>{t('dhanurveda.booking.title')}</h1>
        <p>{t('dhanurveda.booking.subtitle')}</p>
      </div>

      <form className="dv-booking-form" onSubmit={handleSubmit} noValidate>

        {/* Batch selection */}
        <div className="dv-form-section">
          <div className="dv-form-section-title">{t('dhanurveda.booking.section.batch')}</div>
          <div className="dv-batch-select-grid">
            {BATCHES.map(b => (
              <label className={`dv-batch-option${selectedBatch === b.id ? ' selected' : ''}`} key={b.id}>
                <input type="radio" name="batch" value={b.id}
                  checked={selectedBatch === b.id}
                  onChange={() => setSelectedBatch(b.id)} />
                <div className="dv-batch-option-name">{t(`dhanurveda.booking.batch.${b.id}.name`)}</div>
                <div className="dv-batch-option-dates">{t(`dhanurveda.booking.batch.${b.id}.dates`)}</div>
                <div className="dv-batch-option-price">{t(`dhanurveda.booking.batch.${b.id}.price`)}</div>
              </label>
            ))}
          </div>
        </div>

        {/* Personal details */}
        <div className="dv-form-section">
          <div className="dv-form-section-title">{t('dhanurveda.booking.section.personal')}</div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.fullName')} <span className="required">*</span></label>
            <input type="text" value={form.fullName}
              onChange={e => update('fullName', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.fullName')} />
            {errors.fullName && <div style={{color:'#c0392b',fontSize:'0.78rem',marginTop:'0.2rem'}}>{errors.fullName}</div>}
          </div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.email')} <span className="required">*</span></label>
            <input type="email" value={form.email}
              onChange={e => update('email', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.email')} />
            {errors.email && <div style={{color:'#c0392b',fontSize:'0.78rem',marginTop:'0.2rem'}}>{errors.email}</div>}
          </div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.phone')} <span className="required">*</span></label>
            <input type="tel" value={form.phone}
              onChange={e => update('phone', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.phone')} />
            {errors.phone && <div style={{color:'#c0392b',fontSize:'0.78rem',marginTop:'0.2rem'}}>{errors.phone}</div>}
          </div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.city')} <span className="required">*</span></label>
            <input type="text" value={form.city}
              onChange={e => update('city', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.city')} />
            {errors.city && <div style={{color:'#c0392b',fontSize:'0.78rem',marginTop:'0.2rem'}}>{errors.city}</div>}
          </div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.billingCountry')} <span className="required">*</span></label>
            <select
              value={form.billingCountry}
              onChange={e => update('billingCountry', e.target.value as BillingCountryValue)}
            >
              <option value="IN">{t('dhanurveda.booking.option.india')}</option>
              <option value="ZZ">{t('dhanurveda.booking.option.outside')}</option>
            </select>
            <div className="dv-hint">{t('dhanurveda.booking.jurisdiction.helper')}</div>
          </div>
        </div>

        {/* Emergency contact */}
        <div className="dv-form-section">
          <div className="dv-form-section-title">{t('dhanurveda.booking.section.emergency')}</div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.emergencyName')} <span className="required">*</span></label>
            <input type="text" value={form.emergencyName}
              onChange={e => update('emergencyName', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.emergencyName')} />
            {errors.emergencyName && <div style={{color:'#c0392b',fontSize:'0.78rem',marginTop:'0.2rem'}}>{errors.emergencyName}</div>}
          </div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.emergencyPhone')} <span className="required">*</span></label>
            <input type="tel" value={form.emergencyPhone}
              onChange={e => update('emergencyPhone', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.phone')} />
            {errors.emergencyPhone && <div style={{color:'#c0392b',fontSize:'0.78rem',marginTop:'0.2rem'}}>{errors.emergencyPhone}</div>}
          </div>
          <div className="dv-field">
            <label>{t('dhanurveda.booking.field.medicalNotes')}</label>
            <textarea value={form.medicalNotes}
              onChange={e => update('medicalNotes', e.target.value)}
              placeholder={t('dhanurveda.booking.placeholder.medicalNotes')} />
            <div className="dv-hint">{t('dhanurveda.booking.hint.medicalNotes')}</div>
          </div>
        </div>

        {/* Declarations */}
        <div className="dv-form-section">
          <div className="dv-form-section-title">{t('dhanurveda.booking.section.declarations')}</div>

          <div className="dv-checkbox-field">
            <input type="checkbox" id="ageConfirmed" checked={form.ageConfirmed}
              onChange={e => update('ageConfirmed', e.target.checked)} />
            <label htmlFor="ageConfirmed">
              {t('dhanurveda.booking.declaration.age')}
            </label>
          </div>
          {errors.ageConfirmed && <div style={{color:'#c0392b',fontSize:'0.78rem',margin:'-0.5rem 0 0.5rem 1.8rem'}}>{errors.ageConfirmed}</div>}

          <div className="dv-checkbox-field">
            <input type="checkbox" id="healthDeclaration" checked={form.healthDeclaration}
              onChange={e => update('healthDeclaration', e.target.checked)} />
            <label htmlFor="healthDeclaration">
              {t('dhanurveda.booking.declaration.health')}
            </label>
          </div>
          {errors.healthDeclaration && <div style={{color:'#c0392b',fontSize:'0.78rem',margin:'-0.5rem 0 0.5rem 1.8rem'}}>{errors.healthDeclaration}</div>}

          <div className="dv-checkbox-field">
            <input type="checkbox" id="riskAccepted" checked={form.riskAccepted}
              onChange={e => update('riskAccepted', e.target.checked)} />
            <label htmlFor="riskAccepted">
              {t('dhanurveda.booking.declaration.risk')}
            </label>
          </div>
          {errors.riskAccepted && <div style={{color:'#c0392b',fontSize:'0.78rem',margin:'-0.5rem 0 0.5rem 1.8rem'}}>{errors.riskAccepted}</div>}

          <div className="dv-checkbox-field">
            <input type="checkbox" id="termsAccepted" checked={form.termsAccepted}
              onChange={e => update('termsAccepted', e.target.checked)} />
            <label htmlFor="termsAccepted">
              {t('dhanurveda.booking.declaration.termsPrefix')}{' '}
              <span
                style={{color:'#d4700a', textDecoration:'underline', cursor:'pointer'}}
                onClick={() => onNavigate('/rajdharma/dhanurveda/terms')}
              >
                {t('dhanurveda.booking.declaration.termsLink')}
              </span>{' '}
              {t('dhanurveda.booking.declaration.termsSuffix')}
            </label>
          </div>
          {errors.termsAccepted && <div style={{color:'#c0392b',fontSize:'0.78rem',margin:'-0.5rem 0 0.5rem 1.8rem'}}>{errors.termsAccepted}</div>}
        </div>

        {/* Summary */}
        <div className="dv-booking-summary">
          <div className="dv-payment-jurisdiction">
            <div className="dv-payment-jurisdiction-label">{t('dhanurveda.booking.jurisdiction.label')}</div>
            <div className="dv-payment-jurisdiction-note">{paymentSummary.jurisdictionLabel}</div>
          </div>
          <div className="dv-summary-row">
            <span className="dv-summary-label">{t('dhanurveda.booking.summary.session')}</span>
            <span className="dv-summary-value">{batchLabel.name}</span>
          </div>
          <div className="dv-summary-row">
            <span className="dv-summary-label">{t('dhanurveda.booking.summary.dates')}</span>
            <span className="dv-summary-value">{batchLabel.dates}</span>
          </div>
          <div className="dv-summary-row">
            <span className="dv-summary-label">{t('dhanurveda.booking.summary.price')}</span>
            <span className="dv-summary-value">{batchLabel.price}</span>
          </div>
          <div className="dv-summary-row">
            <span className="dv-summary-label">{t('dhanurveda.booking.summary.advance')}</span>
            <span className="dv-summary-value">{formatMoney(batch.advance)}</span>
          </div>
          <div className="dv-summary-row">
            <span className="dv-summary-label">{t('dhanurveda.booking.summary.gst')}</span>
            <span className="dv-summary-value">{formatMoney(paymentSummary.gstAmount)}</span>
          </div>
          <div className="dv-summary-row">
            <span className="dv-summary-label">{t('dhanurveda.booking.summary.total')}</span>
            <span className="dv-summary-value">{formatMoney(paymentSummary.totalAmount)}</span>
          </div>
        </div>

        {paymentError && (
          <div className="dv-payment-error" role="alert">
            {paymentError}
          </div>
        )}
        {paymentStage !== 'idle' && (
          <div className="dv-payment-status" aria-live="polite">
            {paymentStage === 'creating' && t('dhanurveda.booking.processing.order')}
            {paymentStage === 'opening' && t('dhanurveda.booking.processing.checkout')}
            {paymentStage === 'verifying' && t('dhanurveda.booking.processing.verifying')}
          </div>
        )}

        <button
          type="submit"
          className="dv-submit-btn"
          disabled={!form.termsAccepted || !form.riskAccepted || !form.healthDeclaration || !form.ageConfirmed || paymentStage !== 'idle'}
        >
          {t('dhanurveda.booking.button.submit')}
        </button>
        <p style={{fontSize:'0.78rem',color:'#8b5e3c',textAlign:'center',marginTop:'0.75rem'}}>
          {t('dhanurveda.booking.footer.note')}
        </p>
      </form>
    </div>
  );
}
